import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  List,
  ListItem,
  Button,
  Link,
  TextField,
  InputAdornment,
  DialogActions,
  FormControlLabel,
  Checkbox,
  FormHelperText,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import Transition from "../Transition";
import { CrossIcon } from "../../assets/icons";
import {
  AMMLMealPortionResponse,
  AUMSCustomPortionRequestResponse,
  AUMSCustomPortionRequestResponseStatusEnum,
  AUMSMealPortionResponse,
} from "../../openapi/client";
import LoadingButton from "@mui/lab/LoadingButton";
import Api from "../../openapi";
import { useDispatch } from "react-redux";
import { setNotification } from "../../store/notificationSlice";
import { CustomSwitch } from "../DataTable";

interface IProps {
  close: (result?: boolean) => void;
  open: boolean;
  customize?: {
    request?: AUMSCustomPortionRequestResponse;
    current?: AMMLMealPortionResponse;
    portions?: AUMSMealPortionResponse[];
    userId?: string;
    selectionId?: string;
    mealId?: string;
    readOnly?: boolean;
  };
}

interface IPortionItem {
  error?: boolean;
  value?: number;
  message?: string;
}
interface IMealPortion {
  calories?: IPortionItem;
  protein?: IPortionItem;
  carbs?: IPortionItem;
  fat?: IPortionItem;
  portion?: IPortionItem;
}

interface IPortion {
  current: AUMSCustomPortionRequestResponse;
  requested: AUMSCustomPortionRequestResponse;
  portion?: string;
  addTo?: boolean;
  error?: { show: boolean; items: IMealPortion; present: boolean };
}
type CustomKeys = keyof Omit<AUMSCustomPortionRequestResponse, "id" | "status">;

export default function PortionCustomize(props: IProps) {
  const defaultErrorStatus: IMealPortion = {
    calories: {
      error: false,
      message: "Invalid value",
    },
    protein: {
      error: false,
      message: "Invalid value",
    },
    fat: {
      error: false,
      message: "Invalid value",
    },
    carbs: {
      error: false,
      message: "Invalid value",
    },
    portion: {
      error: false,
      message: "Invalid value",
    },
  };
  const defaultReq: IPortion = {
    current: {},
    requested: {},
    portion: "",
    addTo: false,
    error: { items: defaultErrorStatus, show: false, present: false },
  };

  const status =
    props.customize?.request?.status === AUMSCustomPortionRequestResponseStatusEnum.Pending
      ? "Requested"
      : "Adjustment";

  const [saveLoading, setSaveLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const dispatch = useDispatch();
  const [customRequest, setCustomRequest] = useState<IPortion>(defaultReq);
  const [open, setOpen] = useState(props.open);
  const handleClose = (e: React.MouseEvent, reason?: "backdropClick" | "escapeKeyDown") => {
    e.stopPropagation();
    props.close();
  };

  const checkExistingPortion = useMemo(() => {
    return (
      Boolean(customRequest.addTo) &&
      Boolean(props.customize?.portions) &&
      Boolean(props.customize?.portions?.find((i) => i.portion === Number(customRequest.portion)))
    );
  }, [customRequest.addTo, props.customize?.portions, customRequest.portion]);

  const save = async () => {
    if (customRequest.error?.present) {
      setCustomRequest((state) => ({
        ...state,
        error: { items: state.error?.items as IMealPortion, show: true, present: Boolean(state.error?.present) },
      }));
      return;
    }

    if (checkExistingPortion) {
      return;
    }

    try {
      setSaveLoading(true);
      const payload = {
        portion: Number(customRequest.portion),
        calories: Number(customRequest.requested.calories),
        protein: Number(customRequest.requested.protein),
        fat: Number(customRequest.requested.fat),
        carbs: Number(customRequest.requested.carbs),
      };

      // Check if addTo meal portions flag is checked
      if (customRequest.addTo) {
        await Api.AdminMeal.createMealPortion(String(props.customize?.mealId), payload);
      }

      const res = await Api.Selection.approveCustomPortionRequest(
        String(props.customize?.userId),
        String(props.customize?.selectionId),
        payload
      );
      closeRefresh();
    } catch (error: any) {
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: error.response?.data?.message || error.message,
        })
      );
    } finally {
      setSaveLoading(false);
    }
  };

  const adjust = async () => {
    if (customRequest.error?.present) {
      setCustomRequest((state) => ({
        ...state,
        error: { items: state.error?.items as IMealPortion, show: true, present: Boolean(state.error?.present) },
      }));
      return;
    }

    if (checkExistingPortion) {
      return;
    }

    try {
      setSaveLoading(true);

      const payload = {
        portion: Number(customRequest.portion),
        calories: Number(customRequest.requested.calories),
        protein: Number(customRequest.requested.protein),
        fat: Number(customRequest.requested.fat),
        carbs: Number(customRequest.requested.carbs),
      };
      // Check if addTo meal portions flag is checked
      if (customRequest.addTo) {
        await Api.AdminMeal.createMealPortion(String(props.customize?.mealId), payload);
      }

      const res = await Api.Selection.assignCustomPortionRequest(
        String(props.customize?.userId),
        String(props.customize?.selectionId),
        payload
      );
      closeRefresh();
    } catch (error: any) {
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: error.response?.data?.message || error.message,
        })
      );
    } finally {
      setSaveLoading(false);
    }
  };

  const reject = async () => {
    try {
      setRejectLoading(true);
      const res = await Api.Selection.rejectCustomPortionRequest(
        String(props.customize?.userId),
        String(props.customize?.selectionId)
      );
    } catch (error: any) {
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: error.response?.data?.message || error.message,
        })
      );
    } finally {
      setRejectLoading(false);
      closeRefresh();
    }
  };

  const handlePortionChange = (field: CustomKeys | "portion" | "addTo", value: any) => {
    setCustomRequest((state) => {
      let temp = { ...state };
      const numberRegex = /^\d+(\.\d{1,2})?$/;
      const checkIfNumber = Boolean(value.toString().match(numberRegex));
      const checkOutOfRange = Number(value) < 0.5 || Number(value) > 9;

      if (field === "portion") {
        temp.portion = value;
        temp.error!.items.portion!.error = !(checkIfNumber && !checkOutOfRange);
      } else if (field === "addTo") {
        temp.addTo = Boolean(value);
      } else {
        temp.requested[field] = value as any;
        temp.error!.items![field]!.error = !checkIfNumber;
      }

      temp.error!.present = Boolean(
        temp.error?.items.calories?.error ||
          temp.error?.items.protein?.error ||
          temp.error?.items.fat?.error ||
          temp.error?.items.carbs?.error ||
          temp.error?.items.portion?.error
      );
      return temp;
    });
  };

  const closeRefresh = () => {
    props.close(true);
  };

  const reset = () => {
    setCustomRequest(defaultReq);
  };

  useEffect(() => {
    setOpen(props.open);
    setCustomRequest((state) => {
      return {
        ...state,
        current: { ...props.customize?.current },
        requested:
          props.customize?.request?.status === AUMSCustomPortionRequestResponseStatusEnum.Pending
            ? { ...props.customize?.request }
            : { ...props.customize?.current },
        portion: String(props.customize?.current?.portion),
        addTo: false,
      };
    });
  }, [props]);

  // console.log("props", props);

  return (
    <Dialog
      sx={{
        "& .MuiPaper-root": {
          borderRadius: "16px",
          boxShadow: "0px 12px 24px 0px rgba(0, 0, 0, 0.15)",
          padding: "24px",
          width: "100%",
          margin: "4px",
        },
        "& .MuiDialogContent-root,& .MuiDialogActions-root": {
          padding: 0,
        },
        "& .MuiDialogContent-root": {
          color: "text.fontDarkest",
          fontSize: "14px",
          width: "100%",
          display: "flex",
        },
      }}
      open={open}
      TransitionComponent={Transition}
      onClose={handleClose}
    >
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: "text.fontDarkest",
        }}
      >
        <CrossIcon />
      </IconButton>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 500,
              lineHeight: "20px",
              paddingBottom: "16px",
              borderBottomStyle: "solid",
              borderBottomWidth: "1px",
              borderBottomColor: "#ececed",
            }}
          >
            Portion Update
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                padding: "16px 0",
                borderBottom: "1px solid #ececed",
              }}
            >
              <Typography sx={{ fontSize: "14px", fontWeight: 700, lineHeight: "20px", color: "text.dark" }}>
                Current
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: "12px",
                  alignItems: "center",
                  "& .MuiInputBase-readOnly.Mui-readOnly": { backgroundColor: "#EFF0F0" },
                }}
              >
                <Box className="form-section">
                  <Typography component="span" className="form-label">
                    Calories
                  </Typography>
                  <TextField
                    className="form-field"
                    value={customRequest.current?.calories}
                    InputProps={{
                      readOnly: true,
                      endAdornment: <InputAdornment position="end">Cal</InputAdornment>,
                    }}
                  />
                </Box>
                <Box className="form-section">
                  <Typography component="span" className="form-label">
                    Protein
                  </Typography>
                  <TextField
                    className="form-field"
                    value={customRequest.current?.protein}
                    InputProps={{
                      readOnly: true,
                      endAdornment: <InputAdornment position="end">Gr</InputAdornment>,
                    }}
                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                  />
                </Box>
                <Box className="form-section">
                  <Typography component="span" className="form-label">
                    Carbs
                  </Typography>
                  <TextField
                    className="form-field"
                    value={customRequest.current?.carbs}
                    InputProps={{
                      readOnly: true,
                      endAdornment: <InputAdornment position="end">Gr</InputAdornment>,
                    }}
                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                  />
                </Box>
                <Box className="form-section">
                  <Typography component="span" className="form-label">
                    Fat
                  </Typography>
                  <TextField
                    className="form-field"
                    value={customRequest.current?.fat}
                    InputProps={{
                      readOnly: true,
                      endAdornment: <InputAdornment position="end">Gr</InputAdornment>,
                    }}
                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                  />
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                padding: "16px 0",
                borderBottom: "1px solid #ececed",
                "& .changed:after": {
                  content: "''",
                  position: "absolute",
                  right: "5px",
                  top: "5px",
                  borderRadius: "90px",
                  width: "8px",
                  height: "8px",
                  backgroundColor: "text.primary700",
                },
              }}
            >
              <Typography sx={{ fontSize: "14px", fontWeight: 700, lineHeight: "20px", color: "text.dark" }}>
                {status}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: "12px",
                  alignItems: "baseline",
                }}
              >
                <Box className="form-section">
                  <Typography component="span" className="form-label">
                    Calories
                  </Typography>
                  <TextField
                    className={`form-field ${
                      Number(customRequest.requested.calories) !== Number(customRequest.current.calories)
                        ? "changed"
                        : ""
                    }`}
                    value={customRequest.requested?.calories}
                    error={customRequest.error?.show && customRequest.error.items.calories?.error}
                    helperText={
                      customRequest.error?.show && customRequest.error.items.calories?.error
                        ? customRequest.error.items.calories.message
                        : undefined
                    }
                    onChange={(e) => handlePortionChange("calories", e.target.value)}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">Cal</InputAdornment>,
                    }}
                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                  />
                </Box>
                <Box className="form-section">
                  <Typography component="span" className="form-label">
                    Protein
                  </Typography>
                  <TextField
                    className={`form-field ${
                      Number(customRequest.requested.protein) !== Number(customRequest.current.protein) ? "changed" : ""
                    }`}
                    value={customRequest.requested?.protein}
                    error={customRequest.error?.show && customRequest.error.items.protein?.error}
                    helperText={
                      customRequest.error?.show && customRequest.error.items.protein?.error
                        ? customRequest.error.items.protein.message
                        : undefined
                    }
                    onChange={(e) => handlePortionChange("protein", e.target.value)}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">Gr</InputAdornment>,
                    }}
                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                  />
                </Box>
                <Box className="form-section">
                  <Typography component="span" className="form-label">
                    Carbs
                  </Typography>
                  <TextField
                    className={`form-field ${
                      Number(customRequest.requested.carbs) !== Number(customRequest.current.carbs) ? "changed" : ""
                    }`}
                    value={customRequest.requested?.carbs}
                    error={customRequest.error?.show && customRequest.error.items.carbs?.error}
                    helperText={
                      customRequest.error?.show && customRequest.error.items.carbs?.error
                        ? customRequest.error.items.carbs.message
                        : undefined
                    }
                    onChange={(e) => handlePortionChange("carbs", e.target.value)}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">Gr</InputAdornment>,
                    }}
                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                  />
                </Box>
                <Box className="form-section">
                  <Typography component="span" className="form-label">
                    Fat
                  </Typography>
                  <TextField
                    className={`form-field ${
                      Number(customRequest.requested.fat) !== Number(customRequest.current.fat) ? "changed" : ""
                    }`}
                    value={customRequest.requested?.fat}
                    error={customRequest.error?.show && customRequest.error.items.fat?.error}
                    helperText={
                      customRequest.error?.show && customRequest.error.items.fat?.error
                        ? customRequest.error.items.fat.message
                        : undefined
                    }
                    onChange={(e) => handlePortionChange("fat", e.target.value)}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">Gr</InputAdornment>,
                    }}
                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                  />
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                padding: "16px 0",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: "12px",
                  alignItems: "baseline",
                }}
              >
                <Box className="form-section" sx={{ flex: "1 1" }}>
                  <Typography component="span" className="form-label">
                    Portion
                  </Typography>
                  <TextField
                    className={`form-field`}
                    value={customRequest.portion}
                    error={customRequest.error?.show && customRequest.error.items.portion?.error}
                    helperText={
                      customRequest.error?.show && customRequest.error.items.portion?.error
                        ? customRequest.error.items.portion.message
                        : undefined
                    }
                    onChange={(e) => handlePortionChange("portion", e.target.value)}
                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    gap: "8px",
                    flex: "3 3",
                    "& .MuiButton-root": {
                      minWidth: "62px",
                      padding: "14px 0",
                      borderRadius: "8px",
                      color: "text.fontLight3",
                    },
                  }}
                >
                  <Box className="form-section">
                    <Typography component="span" className="form-label">
                      &nbsp;
                    </Typography>
                    <Button onClick={() => handlePortionChange("portion", "0.5")}>0.5</Button>
                  </Box>
                  <Box className="form-section">
                    <Typography component="span" className="form-label">
                      &nbsp;
                    </Typography>
                    <Button onClick={() => handlePortionChange("portion", "1")}>1</Button>
                  </Box>
                  <Box className="form-section">
                    <Typography component="span" className="form-label">
                      &nbsp;
                    </Typography>
                    <Button onClick={() => handlePortionChange("portion", "1.5")}>1.5</Button>
                  </Box>
                  <Box className="form-section">
                    <Typography component="span" className="form-label">
                      &nbsp;
                    </Typography>
                    <Button onClick={() => handlePortionChange("portion", "2")}>2</Button>
                  </Box>
                  <Box className="form-section">
                    <Typography component="span" className="form-label">
                      &nbsp;
                    </Typography>
                    <Button onClick={() => handlePortionChange("portion", "2.5")}>2.5</Button>
                  </Box>
                  <Box className="form-section">
                    <Typography component="span" className="form-label">
                      &nbsp;
                    </Typography>
                    <Button onClick={() => handlePortionChange("portion", "3")}>3</Button>
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={customRequest.addTo}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handlePortionChange("addTo", e.target.checked)
                    }
                    name={"addTo"}
                  />
                }
                label={"Add it to the meal portions"}
                sx={{
                  "&": { margin: 0, width: "auto", columnGap: "4px" },
                  "& > .MuiButtonBase-root": { padding: 0 },
                  "& > .MuiTypography-root": {
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "text.dark2",
                  },
                }}
              />
              {customRequest.addTo &&
              Boolean(props.customize?.portions) &&
              props.customize?.portions?.find((i) => i.portion === Number(customRequest.portion)) ? (
                <FormHelperText sx={{ color: "text.error", margin: 0 }}>
                  {"Selected portion already exists"}
                </FormHelperText>
              ) : null}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        {props.customize?.request?.status === AUMSCustomPortionRequestResponseStatusEnum.Pending ? (
          <>
            <LoadingButton
              fullWidth
              disabled={Boolean(props.customize?.readOnly)}
              loading={rejectLoading}
              color="error"
              variant="contained"
              sx={{ display: "flex", flexDirection: "column", marginTop: "16px" }}
              onClick={reject}
            >
              Reject
            </LoadingButton>
            <LoadingButton
              fullWidth
              disabled={Boolean(props.customize?.readOnly)}
              loading={saveLoading}
              color="primary"
              variant="contained"
              sx={{ display: "flex", flexDirection: "column", marginTop: "16px" }}
              onClick={save}
            >
              Accept
            </LoadingButton>
          </>
        ) : (
          <LoadingButton
            fullWidth
            disabled={Boolean(props.customize?.readOnly)}
            loading={saveLoading}
            color="primary"
            variant="contained"
            sx={{ display: "flex", flexDirection: "column", marginTop: "16px" }}
            onClick={adjust}
          >
            Save
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
}
